import { useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { HOSTS_EMBUTIVEIS, motivoNaoEmbutivel, SUBSISTEMAS_ATIVOS, urlExternaValida, type LinkExterno } from '@malgoos/shared';
import { Botao, Cartao } from '../../componentes/base';
import { Entrada, Rotulo } from '../../componentes/formulario';
import { TextoCortado } from '../../componentes/texto-cortado';
import { get } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import type { PropsSecaoWorkspace } from './Subsistemas';

export const MAX_LINKS = 20;

/** Código novo de link (`link-1`, `link-2`...): estável quando o nome muda e nunca igual ao de um sub-sistema. */
export function novoCodigo(links: readonly Pick<LinkExterno, 'codigo'>[]): string {
  const usados = new Set([...links.map((l) => l.codigo), ...SUBSISTEMAS_ATIVOS]);
  let n = links.length + 1;
  while (usados.has(`link-${n}`)) n++;
  return `link-${n}`;
}

interface PapelResumo {
  id: string;
  nome: string;
}

/**
 * D3 (sub-sistemas do tipo link externo): seção do workspace em Admin > Configurações > Sub-sistemas. Cada link (nome e
 * endereço https) aparece na tela inicial de escolha ao lado dos sub-sistemas e abre em nova aba (base do escritório
 * virtual). Restringir a papéis é opcional (vazio: todos); a lista de papéis aparece para quem vê o Admin. FUN 2026-10-14
 * (cofre embutido): "Abrir dentro do MalgoOS" só para endereço de host permitido (`HOSTS_EMBUTIVEIS`); fora dele, a caixa fica
 * desabilitada com o motivo, e trocar o endereço para um host não permitido desmarca.
 */
export function LinksExternos({ config, aoMudar, aoSalvar, salvando }: PropsSecaoWorkspace) {
  const { t } = useTranslation('configuracoes');
  const s = useSessao();
  const base = useId();
  const links = config.links ?? [];
  const papeis = useQuery({ queryKey: ['/admin/papeis'], queryFn: () => get<{ itens: PapelResumo[] }>('/admin/papeis'), enabled: s.pode('admin.ver') });
  const mudar = (lista: LinkExterno[]) => aoMudar({ ...config, links: lista });
  const editar = (i: number, parte: Partial<LinkExterno>) =>
    mudar(
      links.map((l, j) => {
        if (j !== i) return l;
        const novo = { ...l, ...parte };
        if (novo.embutir && motivoNaoEmbutivel(novo.url)) delete novo.embutir;
        return novo;
      }),
    );
  const invalidos = links.some((l) => !l.nome.trim() || !urlExternaValida(l.url) || (l.embutir && motivoNaoEmbutivel(l.url)));
  const hosts = HOSTS_EMBUTIVEIS.map((h) => h.host).join(', ');

  return (
    <Cartao titulo={t('links.titulo')}>
      <div className="flex min-w-0 flex-col gap-4">
        <p className="text-sm text-tinta-3">{t('links.ajuda')}</p>
        {links.length === 0 && <p className="text-sm text-tinta-3">{t('links.vazio')}</p>}
        <ul className="flex min-w-0 flex-col gap-3">
          {links.map((l, i) => {
            const id = `${base}-${l.codigo}`;
            const urlRuim = l.url !== '' && !urlExternaValida(l.url);
            const motivo = motivoNaoEmbutivel(l.url);
            return (
              <li key={l.codigo} className="flex min-w-0 flex-col gap-3 rounded-lg border border-borda p-3" data-link-config={l.codigo}>
                <div className="grid min-w-0 gap-3 sm:grid-cols-2">
                  <Rotulo id={`${id}-nome`} rotulo={t('links.nome')} obrigatorio>
                    <Entrada id={`${id}-nome`} value={l.nome} maxLength={60} onChange={(e) => editar(i, { nome: e.target.value })} />
                  </Rotulo>
                  <Rotulo id={`${id}-url`} rotulo={t('links.url')} obrigatorio erro={urlRuim ? t('links.urlInvalida') : undefined}>
                    <Entrada
                      id={`${id}-url`}
                      type="url"
                      inputMode="url"
                      placeholder="https://"
                      value={l.url}
                      maxLength={500}
                      aria-invalid={urlRuim || undefined}
                      aria-describedby={urlRuim ? `${id}-url-erro` : undefined}
                      onChange={(e) => editar(i, { url: e.target.value.trim() })}
                    />
                  </Rotulo>
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                  <label className={`flex items-center gap-2 text-sm text-tinta-2 ${motivo ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      data-embutir={l.codigo}
                      checked={!!l.embutir}
                      disabled={!!motivo}
                      aria-describedby={`${id}-embutir-ajuda`}
                      onChange={(e) => editar(i, { embutir: e.target.checked })}
                    />
                    {t('links.embutir')}
                  </label>
                  <p id={`${id}-embutir-ajuda`} className="text-xs text-tinta-3">
                    {motivo ? t(`links.embutirMotivo.${motivo}`, { hosts }) : t('links.embutirAjuda')}
                  </p>
                </div>
                {papeis.data && papeis.data.itens.length > 0 && (
                  <fieldset className="flex min-w-0 flex-col gap-1.5">
                    <legend className="text-sm font-medium text-tinta-2">{t('links.papeis')}</legend>
                    <p className="text-xs text-tinta-3">{t('links.papeisAjuda')}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                      {papeis.data.itens.map((p) => (
                        <label key={p.id} className="flex cursor-pointer items-center gap-2 text-sm text-tinta-2">
                          <input
                            type="checkbox"
                            checked={(l.papeis ?? []).includes(p.id)}
                            onChange={(e) => editar(i, { papeis: e.target.checked ? [...(l.papeis ?? []), p.id] : (l.papeis ?? []).filter((x) => x !== p.id) })}
                          />
                          {p.nome}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                )}
                {/* Review r58 visual B3: nome longo (até 60) a 390 cortava o rótulo fora do cartão; o botão fica na largura do
                    cartão e o rótulo corta com reticências (o title e o nome acessível levam o texto inteiro). */}
                <div className="flex min-w-0 justify-end">
                  <Botao variante="fantasma" tamanho="sm" className="max-w-full" icone={<Trash2 className="size-4" aria-hidden />} onClick={() => mudar(links.filter((_, j) => j !== i))}>
                    <TextoCortado>{t('links.remover', { nome: l.nome || t('links.semNome') })}</TextoCortado>
                  </Botao>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Botao variante="fantasma" disabled={links.length >= MAX_LINKS} icone={<Plus className="size-4" aria-hidden />} onClick={() => mudar([...links, { codigo: novoCodigo(links), nome: '', url: '' }])}>
            {t('links.adicionar')}
          </Botao>
          <Botao carregando={salvando} disabled={invalidos} onClick={aoSalvar}>
            {t('subsistemas.salvar')}
          </Botao>
        </div>
      </div>
    </Cartao>
  );
}
